import React, { useCallback, useEffect, useRef, useState } from "react";
import Editor from "@monaco-editor/react";
import { useTranslation } from "react-i18next";
import { usePythonRunner } from "../lib/pythonRunner";
import { PlayIcon, ResetIcon } from "./Icons";
import TestResults from "./TestResults";

function RuntimeStatus({ status, pythonVersion, onRetry }) {
  const { t } = useTranslation();
  return (
    <span className={`runtime runtime--${status}`}>
      <span className="runtime__dot" aria-hidden="true" />
      {status === "ready" ? t("runtime.ready", { version: pythonVersion || "" }) : t(`runtime.${status}`)}
      {status === "failed" && (
        <button type="button" className="link-button" onClick={onRetry}>
          {t("runtime.retry")}
        </button>
      )}
    </span>
  );
}

function CodeWorkspace({ level, code, onCodeChange, onReset, onSolved, onNext, onReview, hasNext, nextLabel, theme, extraActions, editorPath }) {
  const { t } = useTranslation();
  const { runner, status, pythonVersion } = usePythonRunner();
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const editorRef = useRef(null);

  // Boot Pyodide as soon as a coding level opens, so the first run is faster.
  useEffect(() => {
    runner.start();
  }, [runner]);

  const runTests = useCallback(async () => {
    if (running) return;
    setRunning(true);
    try {
      // Read the editor directly: React state can lag behind the last keystroke when Ctrl+Enter is fast.
      const source = editorRef.current ? editorRef.current.getValue() : code;
      const outcome = await runner.run(source, level.tests);
      setResult(outcome);
      if (outcome.status === "passed") onSolved();
    } finally {
      setRunning(false);
    }
  }, [running, runner, code, level.tests, onSolved]);

  // Monaco keeps the command it was given on mount, so it calls through a ref.
  const runRef = useRef(runTests);
  runRef.current = runTests;

  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
        event.preventDefault();
        runRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleMount = (editor, monaco) => {
    editorRef.current = editor;
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => runRef.current());
  };

  return (
    <section className="workspace" aria-label={t("editor.file")}>
      <div className="toolbar">
        <span className="toolbar__file">{t("editor.file")}</span>
        <RuntimeStatus status={status} pythonVersion={pythonVersion} onRetry={() => runner.restart()} />
        <div className="toolbar__actions">
          {extraActions}
          {confirmingReset ? (
            <span className="confirm">
              <span className="small">{t("editor.resetConfirm")}</span>
              <button
                type="button"
                className="button button--danger button--small"
                onClick={() => {
                  onReset();
                  setConfirmingReset(false);
                  setResult(null);
                }}
              >
                {t("editor.resetYes")}
              </button>
              <button type="button" className="button button--ghost button--small" onClick={() => setConfirmingReset(false)}>
                {t("editor.cancel")}
              </button>
            </span>
          ) : (
            <button type="button" className="button button--ghost button--small" onClick={() => setConfirmingReset(true)}>
              <ResetIcon size={14} />
              {t("editor.reset")}
            </button>
          )}
          <button
            type="button"
            className="button button--primary button--small"
            onClick={runTests}
            disabled={running}
            title={t("editor.shortcut")}
          >
            <PlayIcon size={13} />
            {running ? t("editor.running") : t("editor.run")}
            <kbd>{t("editor.shortcut")}</kbd>
          </button>
        </div>
      </div>

      <div className="editor">
        <Editor
          language="python"
          path={editorPath || `${level.id}.py`}
          value={code}
          onChange={(value) => onCodeChange(value ?? "")}
          onMount={handleMount}
          theme={theme === "dark" ? "vs-dark" : "light"}
          loading={<span className="muted">{t("editor.loading")}</span>}
          options={{
            automaticLayout: true,
            fontFamily: "'JetBrains Mono', 'Fira Code', ui-monospace, monospace",
            fontLigatures: false,
            fontSize: 14,
            lineHeight: 22,
            minimap: { enabled: false },
            padding: { top: 12, bottom: 12 },
            renderLineHighlight: "line",
            scrollBeyondLastLine: false,
            tabSize: 4,
          }}
        />
      </div>

      <TestResults result={result} running={running} onNext={onNext} onReview={onReview} hasNext={hasNext} nextLabel={nextLabel} />
    </section>
  );
}

export default CodeWorkspace;
